import { useState, useEffect, useMemo } from "react";

const OrangeCapTable = ({
  topBatPerformers,
  selectedFilter,
  yearDataLoading = true,
  selectedYear,
}) => {
  const [visibleCount, setVisibleCount] = useState(20);
  const [dataColumns, setDataColumns] = useState([
    { key: "runs_scored", label: "Runs", highlight: "Most Runs" },
    { key: "matches_played", label: "Matches" },
    { key: "innings_played", label: "Inns" },
    { key: "highest_score", label: "HS", highlight: "Best Individual Score" },
    { key: "average", label: "AVG", highlight: "Best Batting Average" },
    { key: "batting_strike_rate", label: "SR", highlight: "Best Strike Rate" },
    { key: "hundred", label: "100s", highlight: "Most 100s" },
    { key: "fifties", label: "50s", highlight: "Most 50s" },
    { key: "sixes", label: "6s", highlight: "Most 6s" },
    { key: "fours", label: "4s", highlight: "Most 4s" },
  ]);

  useEffect(() => {
    let columns = [...dataColumns];
    const selectedIndex = columns.findIndex(
      (item) => item.highlight === selectedFilter
    );
    if (selectedIndex > 0) {
      const [selectedColumn] = columns.splice(selectedIndex, 1);
      columns.unshift(selectedColumn);
    }
    setDataColumns(columns);
    setVisibleCount(20); // Reset to 20 when filter changes
  }, [selectedFilter]);

  const sortedPerformers = useMemo(() => {
    if (!topBatPerformers || !Array.isArray(topBatPerformers)) return [];

    const highlightedColumn = dataColumns.find(
      (col) => col.highlight === selectedFilter
    );

    if (highlightedColumn?.key) {
      return [...topBatPerformers].sort(
        (a, b) => (b[highlightedColumn.key] ?? 0) - (a[highlightedColumn.key] ?? 0)
      );
    }

    return topBatPerformers;
  }, [topBatPerformers, selectedFilter, dataColumns]);

  const handleLoadMore = () => {
    setVisibleCount(sortedPerformers.length); // Load all on click
  };

  return (
    <div className="relative">
      <div className="md:w-full md:overflow-hidden overflow-x-auto">
        <div className="flex py-3 min-w-[800px]">
          <div className="flex w-[35%] text-[#FFFFFF] text-[12px] font-[500]">
            <div className="text-[14px] font-[400] text-[#000000] dark:text-white dark:text-[#FFFFFF80] w-[20%] text-center">Pos</div>
            <div className="text-[14px] font-[400] text-[#000000] dark:text-white dark:text-[#FFFFFF80] w-[80%]">Player Name</div>
          </div>
          <div className="w-[65%] text-center">
            <div className="flex items-center justify-around">
              {dataColumns.map((item, index) => (
                <div
                  key={index}
                  className={`text-[14px] ${selectedFilter === item.highlight ? "font-bold" : "font-[400]"
                    } text-[#000000] dark:text-white w-[24%]`}
                >
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </div>

        {yearDataLoading ? (
          [...Array(5)].map((_, index) => (
            <div key={index} className="flex py-4 min-w-[800px] animate-pulse">
              <div className="flex w-[35%]">
                <div className="bg-gray-300 h-6 w-10 rounded-md mx-auto"></div>
                <div className="flex w-[80%] items-center gap-3">
                  <div className="bg-gray-300 h-[50px] w-[50px] rounded-md"></div>
                  <div>
                    <div className="bg-gray-300 h-4 w-24 rounded-md mb-1"></div>
                    <div className="bg-gray-300 h-3 w-16 rounded-md"></div>
                  </div>
                </div>
              </div>
              <div className="w-[65%] text-center">
                <div className="flex items-center justify-around">
                  {[...Array(9)].map((_, idx) => (
                    <div key={idx} className="bg-gray-300 h-6 w-12 rounded-md"></div>
                  ))}
                </div>
              </div>
            </div>
          ))
        ) : (
          sortedPerformers.slice(0, visibleCount).map((value, index) => (
            <div
              key={index}
              className={`flex ${index % 2 === 0 ? "bg-gray-200 dark:bg-gray-800" : "bg-white dark:bg-[#1F1F1F]"
                } py-3 min-w-[800px] items-center`}
            >
              <div className="flex w-[35%] text-[#FFFFFF] text-[12px] font-[500] items-center">
                <div className="text-[14px] font-[400] text-[#525358] dark:text-[#FFFFFF80] w-[20%] text-center">
                  {index + 1}
                </div>
                <div className="text-[14px] font-[400] text-[#525358] dark:text-[#FFFFFF80] w-[80%]">
                  <div className="flex">
                    <img
                      width="50px"
                      height="50px"
                      className="w-[50px] h-[50px] mr-3 rounded-full"
                      src={`https://akm-img-a-in.tosshub.com/sites/sports/ipl/resources_live/images/players_img/ipl_2025/${value?.player_id}.png`}
                      alt={value?.player_name}
                      onError={(e) => {
                        e.target.src =
                          "https://akm-img-a-in.tosshub.com/sites/sports/ipl/resources_live/images/players_img/ipl_2025/0.png";
                      }}
                    />
                    <div className="font-semibold text-[#525358] dark:text-[#FFFFFFCC]">
                      {value.player_name}
                      <div className="font-normal">{value?.team_short_name ?? "-"}</div>
                    </div>
                    {selectedFilter === "Most Runs" && index === 0 && (
                      <img className="h-5 ml-2" src="https://akm-img-a-in.tosshub.com/lingo/newstak/resources/prod/orange_cap.png" alt="Orange Cap" />
                    )}
                  </div>
                </div>
              </div>
              <div className="w-[65%] text-center">
                <div className="flex items-center justify-around">
                  {dataColumns.map((item, idx) => (
                    <div
                      key={idx}
                      className={`text-[14px] ${selectedFilter === item.highlight
                        ? "font-bold text-[#000] dark:text-white"
                        : "font-[400] text-[#525358] dark:text-[#ffffffcc]"
                        } w-[24%]`}
                    >
                      {value[item.key] ?? "-"}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Load More button */}
      {!yearDataLoading && visibleCount < sortedPerformers.length && (
        <div className="flex justify-center w-full">
          <div>
            <div
              onClick={handleLoadMore}
              className="flex content-center text-center rounded-full h-10 w-auto p-[10px] bg-primary mt-4 text-white dark:text-[#1f1f1f] items-center justify-center cursor-pointer"
            >
              सभी देखें
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrangeCapTable;
